import React, { useCallback, ReactElement } from 'react';
import Card from '../card/card.component';
import CardContent from '../card-content/card-content.component';
import CardHeader from '../card-header/card-header.component';
import LinkText from '../link-text/link-text.component';
import ActionButton from '../action-button/action-button.component';
import TextBlock from '../text-block/text-block.component';
import TextBold from '../text-bold/text-bold.component';
import FlexContainer from '../flex-container/flex-container.component';
import FilteredText from '../filtered-text/filtered-text.component';
import FileLink from '../file-link/file-link.component';
import RichTextBlock from '../rich-text-block/rich-text-block.component';
import ImportantIcon from '../../icons/important-icon';
import MoreIcon from '../../icons/more-icon';
import CloseIcon from '../../icons/close-icon';
import FormatService from '../../services/format-service';
import useStylesRightDrawerContentItem from './right-drawer-content-item.styles';
import { CONTACT_VIEW, SEARCH_RESULT_VIEW } from '../../constants/entity/permissions';
import { DATA_CONTENT_ITEM_ID } from '../../constants/system/data-attributes';
import LINK_TYPE_FILE_LINK from '../../constants/entity/link-types';

const RightDrawerContentItem: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLOR_ERROR: ThemeColor = 'error';

  const {
    isActive,
    isImportant,
    isFeedItem,
    withLinks,
    withMentions,
    withHashtags,
    contentItemId,
    contentText,
    contentDate,
    contentSubject,
    contentImageLink,
    contentHeaderTitle,
    contentHeaderSubTitle,
    contentLinks,
    actionMenuItems,
    onClick,
    onLinkClick,
    onActionMenuOpen,
    onDeleteItem,
  } = props;

  const { classes } = useStylesRightDrawerContentItem();

  const feedClassName: string | undefined = isFeedItem ? classes.rightDrawerContentItemFeed : undefined;

  const contentFileLinks: ContentLink[] = contentLinks && contentLinks.length
    ? contentLinks.filter((contentLink: ContentLink): boolean => contentLink.type === LINK_TYPE_FILE_LINK) : [];

  const linkClick: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onLinkClick) {
      onLinkClick();
    }

    event.stopPropagation();
  }, []);

  const deleteItem: MouseEventHandlerFunction = useCallback((event: React.MouseEvent<Element, MouseEvent>): void => {
    if (onDeleteItem) {
      onDeleteItem(event);
    }
  }, []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    if (onActionMenuOpen) {
      onActionMenuOpen(event);
    }
  }, []);

  return (
    <Card
      isFullWidth={!isFeedItem}
      isActive={isActive}
      withMediumShadow={isFeedItem}
      className={FormatService.formatClassNames(classes.rightDrawerContentItem, feedClassName)}
      onClick={onClick}
    >
      <CardHeader
        isLight={isFeedItem}
        imageLink={contentImageLink}
        imageClassName={isFeedItem ? classes.rightDrawerContentItemHeaderImageFeed : undefined}
        actionComponent={isFeedItem ? (
          <ActionButton isLight onClick={deleteItem}>
            <CloseIcon />
          </ActionButton>
        ) : (
          actionMenuItems && !!actionMenuItems.length && (
            <div>
              <ActionButton
                withPopup
                onClick={openActionMenu}
                {...{
                  [DATA_CONTENT_ITEM_ID]: contentItemId,
                }}
              >
                <MoreIcon />
              </ActionButton>
            </div>
          )
        )}
        headerTitle={contentHeaderTitle}
        headerSubTitle={isFeedItem ? contentDate : (
          <div className={classes.rightDrawerContentItemHeaderSubTitle}>
            <div>
              {contentDate}
            </div>
            {
              contentHeaderSubTitle && (
                <div>
                  {contentHeaderSubTitle}
                </div>
              )
            }
          </div>
        )}
        className={classes.rightDrawerContentItemHeader}
        imageAvatarClassName={!isFeedItem ? classes.rightDrawerContentItemHeaderImageAvatar : undefined}
        headerSubTitleClassName={isFeedItem ? classes.rightDrawerContentItemHeaderSubTitle : undefined}
      />
      <CardContent>
        <FlexContainer className={classes.rightDrawerContentItemSubjectBlock}>
          {
            isImportant && (
              <TextBlock className={classes.rightDrawerContentItemMarkBlock}>
                <ImportantIcon color={COLOR_ERROR} />
              </TextBlock>
            )
          }
          {
            !!contentSubject && (
              <TextBold>
                {contentSubject}
              </TextBold>
            )
          }
        </FlexContainer>
        <RichTextBlock className={classes.rightDrawerContentItemRichTextBlock}>
          {
            withLinks && contentLinks ? (
              <LinkText
                isActive
                links={contentLinks}
                text={contentText && FormatService.formatHtmlToComponent(contentText)}
                onClick={linkClick}
              />
            ) : (
              <FilteredText
                withMentions={withMentions}
                withHashtags={withHashtags}
                text={contentText}
                mentionPermission={CONTACT_VIEW}
                hashtagPermission={SEARCH_RESULT_VIEW}
              />
            )
          }
          {
            contentFileLinks && !!contentFileLinks.length
            && contentFileLinks.map((contentFileLinkItem: ContentLink): ReactElement => (
              <FileLink
                key={contentFileLinkItem.id}
                fileLink={contentFileLinkItem}
              />
            ))
          }
        </RichTextBlock>
      </CardContent>
    </Card>
  );
});

export default RightDrawerContentItem;
